import type { TranslationKeyOrDynamicString } from "@ankaadia/ankaadia-shared";
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { translate } from "@jsverse/transloco";
import { isNil } from "lodash";

/**
 * Safely translate a key with type checking.
 *
 * - Static string literals must be valid TranslationKeys (compile-time check)
 * - Dynamic strings (concatenation, variables) are allowed without validation
 *
 * @example
 * // ✅ Valid TranslationKey - compiles
 * translateSafely("common.save")
 *
 * // ✅ Dynamic string - compiles (no validation)
 * translateSafely("prefix" + dynamicPart)
 * translateSafely(someVariable)
 *
 * // ❌ Invalid literal - compile error
 * translateSafely("invalidKey123")
 */
export function translateSafely<T extends string>(
  key: TranslationKeyOrDynamicString<T>,
  params?: Record<string, any>,
  language?: string
): string {
  if (isNil(key)) {
    return "";
  }

  // Don't ever remove "language ?? undefined". If the default language is passed as null, transloco will not fallback to the default language and throw.
  const safeLanguage = language ?? undefined;
  return translate(key, params, safeLanguage) ?? key;
}
